import { RehabRuleChoiceListFiles } from '@Types';
import { ChoiceListPairs, getFieldOptions } from '@Utils/choiceListFilesUtils';
import { RECORD_TYPE } from '@Utils/choiceListMapping';
import {
	isSystemOfMeasurementUnit,
	isCurrencyUnit,
} from '@Utils/units/unitClassification';
import { TFunc } from '@Translations';

export const getRuleLabel = (
	t: TFunc,
	recordType: string | undefined,
	label: string,
	value: string,
): string => {
	if (recordType && recordType === RECORD_TYPE.RISK) {
		if (value.startsWith('LOFRaw_')) {
			return t(`LoF/${label}/Score`);
		} else if (value.startsWith('COFRaw_')) {
			return t(`CoF/${label}/Score`);
		} else if (value.startsWith('LOF_')) {
			return t(`LoF/${label}/Weighted Score`);
		} else if (value.startsWith('COF_')) {
			return t(`CoF/${label}/Weighted Score`);
		} else {
			return label;
		}
	} else {
		return label;
	}
};

/**
 * Safely format unit values based on unit type
 * - Currency units: Display before value (e.g., $100)
 * - System-of-measurement units: Use getSystemValueFormatted for conversion
 * - Scientific units: Display as-is (no conversion needed)
 *
 * `decimalPlaces` is forwarded to `getSystemValueFormatted` (from
 * `useUnits()`), which caps the value at that many decimal places using
 * `Intl.NumberFormat` with `maximumFractionDigits` (rounding, not
 * truncating) and `minimumFractionDigits: 0` (no trailing-zero padding).
 * This is applied after unit conversion, so it rounds the displayed value
 * rather than the raw pre-conversion value.
 */
export const formatUnitValue = (
	value: string | number,
	unit: string,
	getSystemValueFormatted: (
		value: string,
		invalidValue?: string,
		decimalPlaces?: number,
	) => string | undefined,
	decimalPlaces?: number,
): string => {
	if (!unit) {
		return value.toString();
	}

	if (isCurrencyUnit(unit)) {
		return `${unit}${value}`;
	}

	if (isSystemOfMeasurementUnit(unit)) {
		return (
			getSystemValueFormatted(
				` ${value} ${unit}`,
				undefined,
				decimalPlaces,
			) || `${value} ${unit}`
		);
	}

	return `${value} ${unit}`;
};

/**
 * Resolve multi-select values to their display names
 * Maps raw values (e.g., "1") to their display names (e.g., "Dry - No precipitation during survey")
 */
export const resolveMultiSelectDisplayNames = (
	recordTypeChoiceListFiles: RehabRuleChoiceListFiles,
	recordType: string,
	field: string,
	values: Array<string>,
	config?: string,
): string[] => {
	const fieldOptions = getFieldOptions(
		recordTypeChoiceListFiles,
		recordType,
		field,
		config,
	);

	if (!fieldOptions || fieldOptions.length === 0) {
		return values;
	}

	return values.map(value => {
		const option = fieldOptions.find(
			(opt: ChoiceListPairs) => opt.value === value,
		);
		return option ? option.label : value;
	});
};
